import type { JournalEntryLine } from '@/services/accounting'
import type {
  WarehouseJeReadinessItem,
  WarehouseJournalEntryDraftItem,
} from '@/services/warehouseTypes'

const DRAFT_TYPE_ORDER: Record<string, number> = {
  bk: 0,
  month: 1,
  ist: 2,
  cost: 3,
  fee: 4,
  adj: 5,
}

export function draftItemToJournalLines(
  draft: WarehouseJournalEntryDraftItem
): JournalEntryLine[] {
  return (draft.lines ?? []).map((line) => ({
    account_id: String(line.account_id ?? ''),
    account_name: String(line.account_name ?? ''),
    posting_type: line.posting_type as 'Debit' | 'Credit',
    amount: Number(line.amount ?? 0),
    description: String(line.description ?? ''),
    department_id:
      line.department_id != null ? String(line.department_id) : undefined,
    department_name:
      line.department_name != null ? String(line.department_name) : undefined,
    entity_id: line.entity_id != null ? String(line.entity_id) : undefined,
    entity_name: line.entity_name != null ? String(line.entity_name) : undefined,
    entity_type: line.entity_type != null ? String(line.entity_type) : undefined,
  }))
}

export function isDraftOutOfDateVsLedger(
  draft: WarehouseJournalEntryDraftItem
): boolean {
  const source = draft.source_data
  if (!source) return false
  if (source.needs_qbo_update === true) return true
  if (source.ledger_content_dirty === true) return true
  return false
}

export function sortWarehouseDrafts(
  drafts: WarehouseJournalEntryDraftItem[]
): WarehouseJournalEntryDraftItem[] {
  return [...drafts].sort((left, right) => {
    const leftType = left.draft_type ?? ''
    const rightType = right.draft_type ?? ''
    const leftOrder = DRAFT_TYPE_ORDER[leftType] ?? 99
    const rightOrder = DRAFT_TYPE_ORDER[rightType] ?? 99
    if (leftOrder !== rightOrder) return leftOrder - rightOrder
    return right.created_at.localeCompare(left.created_at)
  })
}

export function draftTypeShortLabel(draftType: string | null | undefined): string {
  switch (draftType) {
    case 'bk':
      return 'BK'
    case 'month':
      return 'Activity'
    case 'ist':
      return 'IST'
    case 'cost':
      return 'Cost'
    case 'fee':
      return 'Fee'
    case 'adj':
      return 'Adj'
    default:
      return draftType?.toUpperCase() ?? 'Draft'
  }
}

const READINESS_LEVEL_ORDER: Record<string, number> = {
  block: 0,
  warn: 1,
  pass: 2,
}

export function sortReadinessItems(
  items: WarehouseJeReadinessItem[]
): WarehouseJeReadinessItem[] {
  return [...items].sort((left, right) => {
    const leftOrder = READINESS_LEVEL_ORDER[left.level] ?? 99
    const rightOrder = READINESS_LEVEL_ORDER[right.level] ?? 99
    return leftOrder - rightOrder
  })
}

export function readinessLevelClass(level: string): string {
  switch (level) {
    case 'pass':
      return 'text-green-800 bg-green-50 border-green-200'
    case 'warn':
      return 'text-amber-900 bg-amber-50 border-amber-200'
    case 'block':
      return 'text-red-800 bg-red-50 border-red-200'
    default:
      return 'text-gray-700 bg-gray-50 border-gray-200'
  }
}

export function pickPreferredDraft(
  drafts: WarehouseJournalEntryDraftItem[],
  preferredId?: number | null
): WarehouseJournalEntryDraftItem | null {
  if (preferredId != null) {
    const match = drafts.find((draft) => draft.id === preferredId)
    if (match) return match
  }
  return drafts.find((draft) => draft.status === 'draft') ?? drafts[0] ?? null
}

export const BK_JE_READINESS_BLOCK_CODES = ['mfr_missing_unit_cost'] as const

export const MONTHLY_JE_READINESS_BLOCK_CODES = [
  'ist_missing_store',
  'ist_missing_unit_cost',
  'draw_missing_unit_cost',
  'return_missing_store',
  'return_missing_unit_cost',
  'adj_missing_unit_cost',
] as const

function hasJeReadinessBlocks(
  items: WarehouseJeReadinessItem[] | undefined,
  codes: readonly string[]
): boolean {
  if (!items?.length) return false
  const codeSet = new Set(codes)
  return items.some((item) => item.level === 'block' && codeSet.has(item.code))
}

export function hasBkJeReadinessBlocks(
  items: WarehouseJeReadinessItem[] | undefined
): boolean {
  return hasJeReadinessBlocks(items, BK_JE_READINESS_BLOCK_CODES)
}

export function hasMonthlyJeReadinessBlocks(
  items: WarehouseJeReadinessItem[] | undefined
): boolean {
  return hasJeReadinessBlocks(items, MONTHLY_JE_READINESS_BLOCK_CODES)
}

export const BK_DRAFT_TYPE = 'bk' as const

export const MONTH_DRAFT_TYPE = 'month' as const

export const MONTHLY_DRAFT_TYPES = ['ist', 'cost', 'fee', 'adj'] as const

export const MONTHLY_SEGMENT_TYPES = [MONTH_DRAFT_TYPE, ...MONTHLY_DRAFT_TYPES] as const

export const MONTH_SECTION_LABELS: Record<string, string> = {
  ist: 'IST',
  cost: 'Cost',
  fee: 'Fees',
  adj: 'Adjustments',
}

function draftReportMonth(draft: WarehouseJournalEntryDraftItem): string | null {
  const fromSource = draft.source_data?.report_month
  return typeof fromSource === 'string' && fromSource.trim() ? fromSource : null
}

function resolveDraftType(draft: WarehouseJournalEntryDraftItem): string | null {
  if (draft.draft_type) return draft.draft_type
  const fromSource = draft.source_data?.draft_type
  return typeof fromSource === 'string' && fromSource.trim() ? fromSource : null
}

export function filterDraftsByMonthAndTypes(
  drafts: WarehouseJournalEntryDraftItem[],
  reportMonth: string,
  types: readonly string[]
): WarehouseJournalEntryDraftItem[] {
  const typeSet = new Set(types)
  return drafts.filter((draft) => {
    const draftType = resolveDraftType(draft)
    if (!draftType || !typeSet.has(draftType)) return false
    return draftReportMonth(draft) === reportMonth
  })
}

export function resolveMonthlyDraft(
  drafts: WarehouseJournalEntryDraftItem[],
  processMonth: string
): WarehouseJournalEntryDraftItem | null {
  const combined = filterDraftsByMonthAndTypes(drafts, processMonth, [MONTH_DRAFT_TYPE])
  if (combined.length > 0) {
    return pickPreferredDraft(combined)
  }
  return null
}

export function resolveLegacyMonthlyDrafts(
  drafts: WarehouseJournalEntryDraftItem[],
  processMonth: string
): WarehouseJournalEntryDraftItem[] {
  return sortWarehouseDrafts(
    filterDraftsByMonthAndTypes(drafts, processMonth, MONTHLY_DRAFT_TYPES)
  )
}

export function isLegacyMonthlyOnly(
  drafts: WarehouseJournalEntryDraftItem[],
  processMonth: string
): boolean {
  return (
    resolveMonthlyDraft(drafts, processMonth) == null &&
    resolveLegacyMonthlyDrafts(drafts, processMonth).length > 0
  )
}

export function lineSectionsFromDraft(
  draft: WarehouseJournalEntryDraftItem
): string[] | undefined {
  const raw = draft.source_data?.line_sections
  if (Array.isArray(raw) && raw.length === (draft.lines?.length ?? 0)) {
    return raw.map((section) =>
      typeof section === 'string' ? MONTH_SECTION_LABELS[section] ?? section : ''
    )
  }
  const sections = draft.source_data?.sections
  if (!Array.isArray(sections) || !draft.lines?.length) return undefined
  const labels = Array.from({ length: draft.lines.length }, () => '')
  for (const section of sections) {
    if (!section || typeof section !== 'object') continue
    const record = section as Record<string, unknown>
    const key = typeof record.section === 'string' ? record.section : ''
    const start = typeof record.line_start === 'number' ? record.line_start : 0
    const count = typeof record.line_count === 'number' ? record.line_count : 0
    const label =
      typeof record.label === 'string' && record.label.trim()
        ? record.label
        : MONTH_SECTION_LABELS[key] ?? key
    for (let i = start; i < start + count && i < labels.length; i += 1) {
      labels[i] = label
    }
  }
  return labels.some((label) => label) ? labels : undefined
}

export function concatLegacyMonthlyPreview(
  drafts: WarehouseJournalEntryDraftItem[]
): { lines: JournalEntryLine[]; lineSections: string[]; drafts: WarehouseJournalEntryDraftItem[] } {
  const ordered = sortWarehouseDrafts(drafts)
  const lines: JournalEntryLine[] = []
  const lineSections: string[] = []
  for (const draft of ordered) {
    const draftType = resolveDraftType(draft) ?? 'draft'
    const label = MONTH_SECTION_LABELS[draftType] ?? draftTypeShortLabel(draftType)
    const draftLines = draftItemToJournalLines(draft)
    for (const line of draftLines) {
      lines.push(line)
      lineSections.push(label)
    }
  }
  return { lines, lineSections, drafts: ordered }
}

export function isBkJeCardCompleted(
  drafts: WarehouseJournalEntryDraftItem[],
  processMonth: string
): boolean {
  return filterDraftsByMonthAndTypes(drafts, processMonth, [BK_DRAFT_TYPE]).some(
    (draft) => draft.status === 'posted'
  )
}

export type ProcessMonthPostingStatus = 'open' | 'partial' | 'complete'

export function getProcessMonthPostingStatus(
  drafts: WarehouseJournalEntryDraftItem[],
  processMonth: string
): ProcessMonthPostingStatus {
  const bk = isBkJeCardCompleted(drafts, processMonth)
  const monthly = isMonthlyJeCardCompleted(drafts, processMonth)
  if (bk && monthly) return 'complete'
  if (bk || monthly) return 'partial'
  return 'open'
}

export function earliestOpenOrPartialProcessMonth(
  months: string[],
  drafts: WarehouseJournalEntryDraftItem[]
): string | null {
  for (const month of months) {
    if (getProcessMonthPostingStatus(drafts, month) !== 'complete') {
      return month
    }
  }
  return null
}

export function processMonthPostingStatusLabel(
  status: ProcessMonthPostingStatus
): 'Open' | 'Partial' | 'Complete' {
  switch (status) {
    case 'open':
      return 'Open'
    case 'partial':
      return 'Partial'
    case 'complete':
      return 'Complete'
  }
}

export function isMonthlyJeCardCompleted(
  drafts: WarehouseJournalEntryDraftItem[],
  processMonth: string
): boolean {
  const combined = filterDraftsByMonthAndTypes(drafts, processMonth, [MONTH_DRAFT_TYPE])
  if (combined.some((draft) => draft.status === 'posted')) return true
  const matching = filterDraftsByMonthAndTypes(
    drafts,
    processMonth,
    MONTHLY_DRAFT_TYPES
  )
  if (matching.length === 0) return false
  const presentTypes = new Set(
    matching.map((draft) => resolveDraftType(draft)).filter(Boolean)
  )
  const allLegacyTypesPresent = MONTHLY_DRAFT_TYPES.every((type) => presentTypes.has(type))
  if (!allLegacyTypesPresent) {
    return matching.length > 0 && matching.every((draft) => draft.status === 'posted')
  }
  return matching.every((draft) => draft.status === 'posted')
}

function draftEffectiveTimestampMs(draft: WarehouseJournalEntryDraftItem): number {
  const created = draft.created_at ? Date.parse(draft.created_at) : Number.NaN
  const posted = draft.posted_at ? Date.parse(draft.posted_at) : Number.NaN
  const contentUpdatedRaw = draft.source_data?.content_updated_at
  const contentUpdated =
    typeof contentUpdatedRaw === 'string' ? Date.parse(contentUpdatedRaw) : Number.NaN
  const validCreated = Number.isFinite(created) ? created : 0
  const validPosted = Number.isFinite(posted) ? posted : 0
  const validContentUpdated = Number.isFinite(contentUpdated) ? contentUpdated : 0
  return Math.max(validCreated, validPosted, validContentUpdated)
}

export type JeCardFooterInfo = {
  timestampLabel: string | null
  qboUrl: string | null
}

export function formatChicagoDateTime(value: Date | string): string | null {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return null

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date)

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? ''

  return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')} ${get('dayPeriod').toUpperCase()}`
}

export function getJeCardFooterInfo(
  drafts: WarehouseJournalEntryDraftItem[]
): JeCardFooterInfo {
  if (drafts.length === 0) {
    return { timestampLabel: null, qboUrl: null }
  }

  const postedDrafts = drafts.filter((draft) => draft.status === 'posted')

  let latestTimestampMs = 0
  let timestampLabel: string | null = null
  for (const draft of postedDrafts) {
    const timestampMs = draftEffectiveTimestampMs(draft)
    if (timestampMs > latestTimestampMs) {
      latestTimestampMs = timestampMs
      timestampLabel = formatChicagoDateTime(new Date(timestampMs))
    }
  }

  const latestPostedWithUrl = [...postedDrafts]
    .filter((draft) => draft.qbo_url)
    .sort((left, right) => draftEffectiveTimestampMs(right) - draftEffectiveTimestampMs(left))[0]

  return {
    timestampLabel,
    qboUrl: latestPostedWithUrl?.qbo_url ?? null,
  }
}
